import {
    PRIMITIVE_LINES,
    Entity,
    Mesh,
    MeshInstance,
    ShaderMaterial,
    Vec3
} from 'playcanvas';

import { Element, ElementType } from './element';
import { isSceneCamera, getSceneCameraData } from './scene-camera';
import type { SceneCameraData } from './scene-camera';
import { vertexShader, fragmentShader } from './shaders/debug-shader';

// Number of line segments to sample along the spline per camera
const PATH_SEGMENTS = 100;

// Colors
const COLOR_PATH = { r: 100, g: 200, b: 255, a: 200 };       // light blue
const COLOR_PATH_SELECTED = { r: 255, g: 255, b: 100, a: 255 }; // yellow

/**
 * Renders camera animation paths as polylines in the viewport.
 * For each scene camera with 2+ keyframes, samples the spline
 * and draws a visible curve.
 */
class CameraPathGizmos extends Element {
    entity: Entity;
    mesh: Mesh;
    material: ShaderMaterial;
    meshInstance: MeshInstance;

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const scene = this.scene;
        const device = scene.graphicsDevice;

        this.material = new ShaderMaterial({
            uniqueName: 'cameraPathGizmoMaterial',
            vertexGLSL: vertexShader,
            fragmentGLSL: fragmentShader
        });
        this.material.depthWrite = true;
        this.material.depthTest = true;
        this.material.update();

        this.mesh = new Mesh(device);
        this.mesh.primitive[0] = {
            baseVertex: 0,
            type: PRIMITIVE_LINES,
            base: 0,
            count: 0
        };

        this.meshInstance = new MeshInstance(this.mesh, this.material, null);
        this.meshInstance.cull = false;

        this.entity = new Entity('cameraPathGizmos');
        this.entity.addComponent('render', {
            meshInstances: [this.meshInstance],
            layers: [scene.worldLayer.id]
        });

        scene.app.root.addChild(this.entity);

        const markDirty = () => {
            scene.forceRender = true;
        };
        const { events } = scene;
        events.on('track.keysLoaded', markDirty);
        events.on('track.keyAdded', markDirty);
        events.on('track.keyRemoved', markDirty);
        events.on('track.keyMoved', markDirty);
        events.on('selection.changed', markDirty);
        events.on('helpers.gizmos', markDirty);
        events.on('hierarchy.changed', markDirty);
        events.on('scene.elementRemoved', markDirty);
        events.on('helper.visibility', markDirty);
    }

    destroy() {
        this.entity?.destroy();
    }

    onPreRender() {
        const { scene } = this;
        this.entity.enabled = scene.camera.renderOverlays;

        if (this.entity.enabled) {
            this.rebuildMesh();
        }
    }

    private rebuildMesh() {
        const { scene } = this;
        const helpers = scene.getElementsByType(ElementType.helper);
        const cameras = helpers.filter(h => isSceneCamera(h));
        const selection = scene.events.invoke('selection');
        const showAllPaths = !!scene.events.invoke('helpers.gizmos');

        // Filter to cameras that have a displayable path (2+ keyframes)
        const withPaths = cameras.filter(cam => {
            const data = getSceneCameraData(cam) as SceneCameraData | null;
            if (!data) return false;
            return data.animTrack.keys.length >= 2 && (showAllPaths || cam === selection);
        });

        if (withPaths.length === 0) {
            this.mesh.primitive[0].count = 0;
            return;
        }

        const positions: number[] = [];
        const colorData: number[] = [];

        for (const cam of withPaths) {
            const data = getSceneCameraData(cam) as SceneCameraData;
            const track = data.animTrack;
            const keys = track.keys;
            if (keys.length < 2) continue;

            const isSelected = cam === selection;
            const clr = isSelected ? COLOR_PATH_SELECTED : COLOR_PATH;

            // Sample range: from first keyframe to last keyframe
            const firstFrame = keys[0];
            const lastFrame = keys[keys.length - 1];
            const range = lastFrame - firstFrame;
            if (range <= 0) continue;

            const segments = Math.min(PATH_SEGMENTS, Math.max(20, Math.round(range / 2)));

            let prevPos: Vec3 | null = null;

            for (let i = 0; i <= segments; i++) {
                const frame = firstFrame + (i / segments) * range;
                const result = track.evaluate(frame);
                if (!result) continue;

                const pos = result.position;

                if (prevPos) {
                    // Line segment from prevPos to pos
                    positions.push(prevPos.x, prevPos.y, prevPos.z);
                    positions.push(pos.x, pos.y, pos.z);
                    colorData.push(clr.r, clr.g, clr.b, clr.a);
                    colorData.push(clr.r, clr.g, clr.b, clr.a);
                }

                prevPos = pos.clone();
            }
        }

        if (positions.length === 0) {
            this.mesh.primitive[0].count = 0;
            return;
        }

        const numVerts = positions.length / 3;
        const colors = new Uint8Array(colorData);

        this.mesh.setPositions(positions);
        this.mesh.setColors32(colors);
        this.mesh.update(PRIMITIVE_LINES);
    }
}

export { CameraPathGizmos };
